/**
 * A unit's name, split into the part that names the machine and the part that
 * names the variant.
 *
 * Its own module, importing nothing, and that is the point: `preview.ts` also
 * wants `chassisOf`, but it names every unit sprite the site has, so anything
 * importing it eagerly pulls that whole map into the front page's first
 * bundle. `analytics.test.mjs` holds a ceiling on exactly that.
 */

/**
 * The chassis: the head of the name, with the variant's designation dropped.
 *
 * "Thor (Summoner) A" reads better as its parenthetical-free head; the count
 * suffix ("×4") is display text from the catalog and is kept.
 */
export function chassisOf(unit: string): string {
  const count = / ×\d+$/.exec(unit)?.[0] ?? "";
  const head = unit.replace(/ ×\d+$/, "");
  const words = head.split(" ");
  // Drop trailing model codes: a word with a digit, or a single letter.
  while (
    words.length > 1 &&
    (/[0-9]/.test(words[words.length - 1]!) ||
      /^[A-Z]$/.test(words[words.length - 1]!) ||
      /^\(.*\)$/.test(words[words.length - 1]!) ||
      words[words.length - 1] === "Prime" ||
      words[words.length - 1] === "(Standard)")
  ) {
    words.pop();
  }
  return words.join(" ") + count;
}

/**
 * The designation, without the chassis: `AS7-D` of `Atlas AS7-D`.
 *
 * What is left after the chassis can still be an alias rather than a model —
 * `Hellhound (Conjurer) (Standard)` leaves `(Conjurer) (Standard)`, which
 * identifies nothing and is wider than the machine it labels. Parentheticals
 * go, and what remains is the designation or nothing.
 */
export function modelOf(unit: string, chassis = chassisOf(unit)): string {
  return unit
    .slice(chassis.length)
    .replace(/\([^)]*\)/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
